import {
  Alert,
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Paper,
  SelectChangeEvent,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LayoutDashboard, List, RefreshCw, Search } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useGetCompanyOpportunitiesQuery } from "../../../../store/services/opportunityService";
import OpportunityCardSkeleton from "../../card/OpportunityCardSkeleton";
import OpportunityItem from "../../card/OpportunityItem";
import OpportunityMenu from "../../card/OpportunityMenu";
import FilterSection from "./FilterSection";
import { calculateStatistics } from "./helpers";
import OpportunityDashboard from "./OpportunityDashboard";
import StatisticsCards from "./StatisticsCards";
import { FilterOptions } from "./types";

const CompanyOpportunitiesView: React.FC = () => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [viewMode, setViewMode] = useState<"list" | "dashboard">("dashboard");

  // Filter state
  const [filters, setFilters] = useState<FilterOptions>({
    startDate: null,
    endDate: null,
    sortBy: "newest",
    searchTerm: "",
    assignedTo: "",
    customerSegment: "", // Ny tom filter
  });

  const queryParams = {
    startDate: filters.startDate,
    endDate: filters.endDate,
  };

  const {
    data: opportunities = [],
    isLoading,
    error,
    refetch,
  } = useGetCompanyOpportunitiesQuery(queryParams);

  // Extract unique assigned users from opportunities for filter dropdown
  const uniqueAssignees = useMemo(() => {
    if (!opportunities || opportunities.length === 0) return [];

    const assignees = opportunities
      .filter((opp) => opp.WorkAssignedTo && opp.AssignedToName)
      .map((opp) => ({
        id: opp.WorkAssignedTo,
        name: opp.AssignedToName,
      }));

    // Remove duplicates using Set and id property
    const uniqueIds = new Set();
    return assignees.filter((assignee) => {
      if (uniqueIds.has(assignee.id)) return false;
      uniqueIds.add(assignee.id);
      return true;
    });
  }, [opportunities]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleSegmentChange = (event: SelectChangeEvent) => {
    setFilters({
      ...filters,
      customerSegment: event.target.value,
    });
  };

  const uniqueSegments = useMemo(() => {
    if (!opportunities || opportunities.length === 0) return [];

    const segments = opportunities
      .filter((opp) => opp.CustomerSegmentName) // Filtrera bort null/undefined
      .map((opp) => opp.CustomerSegmentName as string); // Type assertion för att hantera null/undefined

    // Ta bort dubletter med Set
    return Array.from(new Set(segments)).sort();
  }, [opportunities]);

  const handleDateChange =
    (field: keyof FilterOptions) => (newValue: Date | null) => {
      setFilters({
        ...filters,
        [field]: newValue,
      });
    };

  const handleSortChange = (event: SelectChangeEvent) => {
    setFilters({
      ...filters,
      sortBy: event.target.value,
    });
  };

  const handleAssigneeChange = (event: SelectChangeEvent) => {
    setFilters({
      ...filters,
      assignedTo: event.target.value,
    });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({
      ...filters,
      searchTerm: event.target.value,
    });
  };

  const handleViewChange = (_event: React.SyntheticEvent, newValue: number) => {
    setViewMode(newValue === 0 ? "dashboard" : "list");
  };

  // Sort and filter opportunities
  const filteredAndSortedOpportunities = useMemo(() => {
    // First apply search filter
    let filtered = opportunities;

    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (opp) =>
          opp.OpportunityName.toLowerCase().includes(searchLower) ||
          (opp.OpportunityDescription &&
            opp.OpportunityDescription.toLowerCase().includes(searchLower)) ||
          (opp.CustomerName &&
            opp.CustomerName.toLowerCase().includes(searchLower))
      );
    }

    // Filter by assigned user
    if (filters.assignedTo) {
      filtered = filtered.filter(
        (opp) => opp.AssignedToName === filters.assignedTo
      );
    }

    // Filter by customer segment (ny filter)
    if (filters.customerSegment) {
      filtered = filtered.filter(
        (opp) => opp.CustomerSegmentName === filters.customerSegment
      );
    }

    // Then sort
    return [...filtered].sort((a, b) => {
      switch (filters.sortBy) {
        case "newest":
          return new Date(b.Created).getTime() - new Date(a.Created).getTime();
        case "oldest":
          return new Date(a.Created).getTime() - new Date(b.Created).getTime();
        case "value-high":
          return (
            (b.OpportunityTotalAmount || 0) - (a.OpportunityTotalAmount || 0)
          );
        case "value-low":
          return (
            (a.OpportunityTotalAmount || 0) - (b.OpportunityTotalAmount || 0)
          );
        case "probability-high":
          return (
            (b.OpportunityProbability || 0) - (a.OpportunityProbability || 0)
          );
        case "probability-low":
          return (
            (a.OpportunityProbability || 0) - (b.OpportunityProbability || 0)
          );
        case "name-asc":
          return a.OpportunityName.localeCompare(b.OpportunityName);
        case "name-desc":
          return b.OpportunityName.localeCompare(a.OpportunityName);
        default:
          return 0;
      }
    });
  }, [opportunities, filters]);

  // Beräkna statistik
  const stats = useMemo(
    () => calculateStatistics(filteredAndSortedOpportunities),
    [filteredAndSortedOpportunities]
  );

  if (isLoading) {
    return <OpportunityCardSkeleton />;
  }

  if (error) {
    return (
      <Card elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <CardContent>
          <Alert severity="error">
            Ett fel uppstod vid hämtning av Affärsmöjligheter
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Top Filter Section */}
      <Paper elevation={3} sx={{ mb: 3, p: 2, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h5">Affärsmöjligheter</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Sök">
                  <TextField
                    placeholder="Sök..."
                    size="small"
                    value={filters.searchTerm}
                    onChange={handleSearchChange}
                    sx={{ width: { xs: 120, sm: 200 } }}
                    InputProps={{
                      startAdornment: (
                        <Search size={18} style={{ marginRight: 8 }} />
                      ),
                    }}
                  />
                </Tooltip>
                <Tooltip title="Uppdatera">
                  <IconButton onClick={() => refetch()}>
                    <RefreshCw size={20} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <FilterSection
              filters={filters}
              uniqueAssignees={uniqueAssignees}
              uniqueSegments={uniqueSegments} // Lägg till segmentlistan
              handleDateChange={handleDateChange}
              handleSortChange={handleSortChange}
              handleAssigneeChange={handleAssigneeChange}
              handleSegmentChange={handleSegmentChange} // Lägg till handler för segmentfilter
            />
          </Grid>
        </Grid>
      </Paper>

      {/* View Switcher */}
      <Box sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={viewMode === "dashboard" ? 0 : 1}
          onChange={handleViewChange}
        >
          <Tab
            icon={<LayoutDashboard size={16} />}
            iconPosition="start"
            label="Dashboard"
          />
          <Tab icon={<List size={16} />} iconPosition="start" label="Lista" />
        </Tabs>
      </Box>

      {/* Dashboard View */}
      {viewMode === "dashboard" && (
        <OpportunityDashboard opportunities={filteredAndSortedOpportunities} />
      )}

      {/* List View */}
      {viewMode === "list" && (
        <>
          <StatisticsCards
            stats={stats}
            filteredAndSortedOpportunities={filteredAndSortedOpportunities}
          />

          <Card
            elevation={2}
            sx={{ borderRadius: 2, overflow: "hidden", mb: 3 }}
          >
            <CardContent sx={{ p: 0 }}>
              {filteredAndSortedOpportunities.length === 0 ? (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Typography variant="body1" color="text.secondary">
                    Inga Affärsmöjligheter hittades
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Justera filtren eller välj ett annat datumintervall
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={0}>
                  {filteredAndSortedOpportunities.map((opportunity, idx) => (
                    <OpportunityItem
                      index={idx}
                      key={opportunity.OpportunityNo}
                      opportunity={opportunity}
                      onMenuOpen={(e) =>
                        handleMenuOpen(e,)
                      }
                    />
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <OpportunityMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      />
    </>
  );
};

export default CompanyOpportunitiesView;
